import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, Ticket, Crown, Sailboat, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PAY_BADGE = { paid: "bg-emerald-500/20 text-emerald-300", refunded: "bg-zinc-500/20 text-zinc-300", partially_refunded: "bg-amber-500/20 text-amber-300", cancelled: "bg-zinc-500/20 text-zinc-300" };

export function MyMarketplace() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("orders");
  const [refund, setRefund] = useState(null);
  const [reason, setReason] = useState("");

  const load = () => api.get("/my/marketplace").then((r) => setData(r.data)).catch(() => setData({ orders: [], tickets: [], memberships: [], bookings: [] }));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-rose-500" /></div>;

  const tabs = [
    ["orders", "Orders", Package, data.orders.length],
    ["tickets", "Tickets", Ticket, data.tickets.length],
    ["memberships", "Memberships", Crown, data.memberships.length],
    ["bookings", "Bookings", Sailboat, data.bookings.length],
  ];

  const requestRefund = async () => {
    try {
      await api.post(`/marketplace/orders/${refund.id}/refund-request`, { reason });
      toast.success("Refund request submitted");
      setRefund(null); setReason(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const cancelMembership = async (m) => {
    try {
      const { data: d } = await api.post(`/marketplace/memberships/${m.id}/cancel`);
      toast.success(d.message);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="my-marketplace">
      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map(([k, l, Icon, n]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`myorders-tab-${k}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border ${tab === k ? "bg-rose-500 border-rose-500" : "border-white/15 text-zinc-400 hover:text-white"}`}>
            <Icon size={15} /> {l} {n > 0 && <span className="text-xs opacity-70">({n})</span>}
          </button>
        ))}
      </div>

      {tab === "orders" && (data.orders.length === 0 ? <Empty label="No orders yet." /> : data.orders.map((o) => (
        <div key={o.id} className="bg-[#111] border border-white/10 rounded-2xl p-5 mb-3" data-testid={`myorder-${o.order_number}`}>
          <div className="flex flex-wrap items-center gap-2 justify-between mb-2">
            <p className="font-bold">{o.order_number}</p>
            <div className="flex gap-2">
              <span className={`text-xs px-2.5 py-1 rounded-full capitalize ${PAY_BADGE[o.payment_status] || "bg-white/10 text-zinc-300"}`}>{o.payment_status.replace("_", " ")}</span>
              {o.fulfillment_status !== "not_required" && <span className="text-xs px-2.5 py-1 rounded-full bg-white/10 text-zinc-300 capitalize">{o.fulfillment_status.replace(/_/g, " ")}</span>}
            </div>
          </div>
          <p className="text-xs text-zinc-500 mb-2">Sold by {o.seller_name} · {new Date(o.created_at).toLocaleDateString()}</p>
          {o.items.map((it, i) => <p key={i} className="text-sm text-zinc-300">{it.qty} × {it.product_name}{it.variation_name ? ` (${it.variation_name})` : ""}</p>)}
          {o.tracking_number && <p className="text-xs text-zinc-400 mt-2">Tracking: <span className="font-mono">{o.tracking_number}</span>{o.shipping_carrier ? ` (${o.shipping_carrier})` : ""}</p>}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
            <span className="font-bold">${o.total.toFixed(2)}</span>
            {o.payment_status === "paid" && (
              <button onClick={() => setRefund(o)} className="text-xs text-zinc-400 hover:text-rose-400" data-testid={`myorder-refund-${o.order_number}`}>Request refund</button>
            )}
          </div>
        </div>
      )))}

      {tab === "tickets" && (data.tickets.length === 0 ? <Empty label="No tickets yet." /> : (
        <div className="grid sm:grid-cols-2 gap-4">
          {data.tickets.map((t) => (
            <div key={t.id} className="bg-[#111] border border-white/10 rounded-2xl p-5 text-center" data-testid={`myticket-${t.code}`}>
              {t.qr_data_url && <img src={t.qr_data_url} alt="QR" className="w-32 h-32 mx-auto rounded-lg bg-white p-1" />}
              <p className="font-bold mt-3 text-sm">{t.product_name}</p>
              <p className="text-xs text-zinc-400">{t.attendee_name}{t.event_date ? ` · ${t.event_date}` : ""}</p>
              <p className="font-mono text-rose-400 text-sm mt-1">{t.code}</p>
              <span className={`inline-block mt-2 text-xs px-2.5 py-1 rounded-full capitalize ${t.checkin_status === "unused" ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-500/20 text-zinc-300"}`}>{t.checkin_status.replace("_", " ")}</span>
            </div>
          ))}
        </div>
      ))}

      {tab === "memberships" && (data.memberships.length === 0 ? <Empty label="No memberships yet." /> : data.memberships.map((m) => (
        <div key={m.id} className="bg-[#111] border border-white/10 rounded-2xl p-5 mb-3" data-testid={`mymembership-${m.id}`}>
          <div className="flex items-center justify-between mb-1">
            <p className="font-bold">{m.product_name}</p>
            <span className={`text-xs px-2.5 py-1 rounded-full capitalize ${m.status === "active" ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-500/20 text-zinc-300"}`}>{m.status}</span>
          </div>
          <p className="text-xs text-zinc-500">{m.seller_name} · ${Number(m.price).toFixed(2)}/{m.billing_interval}</p>
          <p className="text-xs text-zinc-400 mt-1">{m.cancel_at_period_end ? "Ends" : "Renews"}: {new Date(m.current_period_end).toLocaleDateString()}</p>
          {(m.benefits || []).length > 0 && <p className="text-xs text-zinc-400 mt-2">Benefits: {m.benefits.join(" · ")}</p>}
          {m.status === "active" && !m.cancel_at_period_end && (
            <button onClick={() => cancelMembership(m)} className="mt-3 text-xs text-zinc-400 hover:text-rose-400" data-testid={`membership-cancel-${m.id}`}>Cancel membership</button>
          )}
        </div>
      )))}

      {tab === "bookings" && (data.bookings.length === 0 ? <Empty label="No activity bookings yet." /> : data.bookings.map((b) => (
        <div key={b.id} className="bg-[#111] border border-white/10 rounded-2xl p-5 mb-3">
          <div className="flex items-center justify-between mb-1">
            <p className="font-bold">{b.activity_name}</p>
            <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 capitalize">{b.status}</span>
          </div>
          <p className="text-xs text-zinc-400">{b.schedule_date} {b.schedule_time} · Party of {b.party_size} · Paid ${Number(b.amount_paid || 0).toFixed(2)}</p>
        </div>
      )))}

      <Dialog open={!!refund} onOpenChange={(v) => !v && setRefund(null)}>
        <DialogContent className="bg-[#111] border-white/10">
          <DialogHeader><DialogTitle>Request refund — {refund?.order_number}</DialogTitle></DialogHeader>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why are you requesting a refund?"
            className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 outline-none focus:border-rose-500 text-sm min-h-[90px]" data-testid="refund-reason" />
          <button onClick={requestRefund} className="bg-rose-500 hover:bg-rose-600 font-bold py-2.5 rounded-full" data-testid="refund-submit">Submit request</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Empty = ({ label }) => <div className="py-16 text-center text-zinc-500">{label} <Link to="/marketplace" className="text-rose-400">Browse the marketplace</Link></div>;

export default function MyOrders() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  useEffect(() => { if (!loading && !user) nav("/login"); }, [user, loading, nav]);
  if (!user) return <div className="py-40 text-center text-zinc-400">Loading...</div>;
  return (
    <div className="px-6 md:px-12 lg:px-20 py-12 min-h-screen" data-testid="my-orders-page">
      <h1 className="font-display font-extrabold text-3xl md:text-4xl mb-8">My marketplace</h1>
      <MyMarketplace />
    </div>
  );
}
